import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
    TrendingUp,
    TrendingDown,
    Target,
    DollarSign,
    Users,
    BarChart3,
    Calendar,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';

const RevenuePerformanceSummary = ({ monthlyData, metrics, loading = false }) => {
    if (loading) {
        return (
            <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                        <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                            <BarChart3 className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                        </div>
                        <span className="text-foreground/90 font-medium">Performance Summary</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        <div className="animate-pulse space-y-2">
                            <div className="h-4 bg-muted rounded w-3/4"></div>
                            <div className="h-4 bg-muted rounded w-1/2"></div>
                            <div className="h-4 bg-muted rounded w-2/3"></div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!monthlyData || monthlyData.length === 0) {
        return (
            <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                        <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                            <BarChart3 className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                        </div>
                        <span className="text-foreground/90 font-medium">Performance Summary</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                        <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-sm">No performance data available</p>
                        <p className="text-xs text-muted-foreground/70 mt-1">Start generating revenue to see performance metrics</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Calculate performance metrics
    const totalRevenue = monthlyData.reduce((sum, item) => sum + (item.revenue || 0), 0);
    const totalDownloads = monthlyData.reduce((sum, item) => sum + (item.downloads || 0), 0);
    const totalSubscriptions = monthlyData.reduce((sum, item) => sum + (item.subscriptions || 0), 0);
    const avgDailyRevenue = totalRevenue / monthlyData.length;
    const avgDailyDownloads = totalDownloads / monthlyData.length;
    const avgDailySubscriptions = totalSubscriptions / monthlyData.length;

    // Calculate trends (comparing first half vs second half of data)
    const midPoint = Math.floor(monthlyData.length / 2);
    const firstHalf = monthlyData.slice(0, midPoint);
    const secondHalf = monthlyData.slice(midPoint);

    const firstHalfRevenue = firstHalf.reduce((sum, item) => sum + (item.revenue || 0), 0);
    const secondHalfRevenue = secondHalf.reduce((sum, item) => sum + (item.revenue || 0), 0);
    const revenueTrend = firstHalfRevenue > 0 ? ((secondHalfRevenue - firstHalfRevenue) / firstHalfRevenue) * 100 : 0;

    const firstHalfDownloads = firstHalf.reduce((sum, item) => sum + (item.downloads || 0), 0);
    const secondHalfDownloads = secondHalf.reduce((sum, item) => sum + (item.downloads || 0), 0);
    const downloadTrend = firstHalfDownloads > 0 ? ((secondHalfDownloads - firstHalfDownloads) / firstHalfDownloads) * 100 : 0;

    const firstHalfSubscriptions = firstHalf.reduce((sum, item) => sum + (item.subscriptions || 0), 0);
    const secondHalfSubscriptions = secondHalf.reduce((sum, item) => sum + (item.subscriptions || 0), 0);
    const subscriptionTrend = firstHalfSubscriptions > 0 ? ((secondHalfSubscriptions - firstHalfSubscriptions) / firstHalfSubscriptions) * 100 : 0;

    // Calculate conversion rates
    const totalPaywallViews = totalDownloads * 2.5; // Estimate based on typical conversion rates
    const downloadConversionRate = totalPaywallViews > 0 ? (totalDownloads / totalPaywallViews) * 100 : 0;
    const subscriptionConversionRate = totalPaywallViews > 0 ? (totalSubscriptions / totalPaywallViews) * 100 : 0;

    // Performance indicators
    const performanceIndicators = [
        {
            label: 'Revenue Growth',
            value: revenueTrend,
            icon: revenueTrend >= 0 ? TrendingUp : TrendingDown,
            color: revenueTrend >= 0 ? 'text-green-600' : 'text-red-600',
            bgColor: revenueTrend >= 0 ? 'bg-green-50' : 'bg-red-50',
            format: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`
        },
        {
            label: 'Download Growth',
            value: downloadTrend,
            icon: downloadTrend >= 0 ? TrendingUp : TrendingDown,
            color: downloadTrend >= 0 ? 'text-green-600' : 'text-red-600',
            bgColor: downloadTrend >= 0 ? 'bg-green-50' : 'bg-red-50',
            format: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`
        },
        {
            label: 'Subscription Growth',
            value: subscriptionTrend,
            icon: subscriptionTrend >= 0 ? TrendingUp : TrendingDown,
            color: subscriptionTrend >= 0 ? 'text-green-600' : 'text-red-600',
            bgColor: subscriptionTrend >= 0 ? 'bg-green-50' : 'bg-red-50',
            format: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`
        }
    ];

    // Key metrics
    const keyMetrics = [
        {
            label: 'Total Revenue',
            value: totalRevenue,
            icon: DollarSign,
            color: 'text-green-600',
            format: (v) => `$${Math.round(v).toLocaleString()}`
        },
        {
            label: 'Avg Daily Revenue',
            value: avgDailyRevenue,
            icon: Calendar,
            color: 'text-blue-600',
            format: (v) => `$${Math.round(v).toLocaleString()}`
        },
        {
            label: 'Total Downloads',
            value: totalDownloads,
            icon: Users,
            color: 'text-purple-600',
            format: (v) => Math.round(v).toLocaleString()
        },
        {
            label: 'Total Subscriptions',
            value: totalSubscriptions,
            icon: Target,
            color: 'text-orange-600',
            format: (v) => Math.round(v).toLocaleString()
        }
    ];

    return (
        <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                    <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                        <BarChart3 className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                    </div>
                    <span className="text-foreground/90 font-medium">Performance Summary</span>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {keyMetrics.map((metric, index) => (
                        <div key={index} className="text-center space-y-2">
                            <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${metric.color} bg-opacity-10`}>
                                <metric.icon className="w-4 h-4" />
                            </div>
                            <div className="text-lg font-semibold">
                                {metric.format(metric.value)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {metric.label}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Performance Indicators */}
                <div className="space-y-4">
                    <h4 className="text-sm font-medium text-foreground/80">Growth Trends</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {performanceIndicators.map((indicator, index) => (
                            <div key={index} className={`p-4 rounded-lg ${indicator.bgColor} border`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-foreground/80">
                                        {indicator.label}
                                    </span>
                                    <indicator.icon className={`w-4 h-4 ${indicator.color}`} />
                                </div>
                                <div className={`text-lg font-semibold ${indicator.color}`}>
                                    {indicator.format(indicator.value)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Conversion Rates */}
                <div className="space-y-4">
                    <h4 className="text-sm font-medium text-foreground/80">Conversion Performance</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Download Conversion</span>
                                <span className="font-medium">{downloadConversionRate.toFixed(1)}%</span>
                            </div>
                            <Progress value={Math.min(downloadConversionRate, 100)} className="h-2" />
                            <div className="text-xs text-muted-foreground">
                                {Math.round(totalDownloads)} downloads from {Math.round(totalPaywallViews)} views
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Subscription Conversion</span>
                                <span className="font-medium">{subscriptionConversionRate.toFixed(1)}%</span>
                            </div>
                            <Progress value={Math.min(subscriptionConversionRate, 100)} className="h-2" />
                            <div className="text-xs text-muted-foreground">
                                {Math.round(totalSubscriptions)} subscriptions from {Math.round(totalPaywallViews)} views
                            </div>
                        </div>
                    </div>
                </div>

                {/* Performance Summary */}
                <div className="pt-4 border-t border-border/50">
                    <div className="flex items-center gap-2 mb-3">
                        <BarChart3 className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground/80">Performance Summary</span>
                    </div>
                    <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Data Period</span>
                            <span className="font-medium">{monthlyData.length} days</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Revenue per Day</span>
                            <span className="font-medium">${Math.round(avgDailyRevenue).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Downloads per Day</span>
                            <span className="font-medium">{Math.round(avgDailyDownloads).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Subscriptions per Day</span>
                            <span className="font-medium">{Math.round(avgDailySubscriptions).toLocaleString()}</span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default RevenuePerformanceSummary; 